// src/lib/createCasePhases.js  ← REPLACE YOUR ENTIRE FILE WITH THIS

import { supabase } from './supabase';
import { fetchTemplateNameOptionsByMilestoneIds } from '../services/milestoneNameOptionsService';
import { resolveInitialMilestoneName } from './milestoneNameOptions';

/**
 * Fetches all active phase templates (with their milestone templates) and
 * creates the corresponding rows in case_timeline_phases + phase_milestones.
 *
 * @param {string} caseId          - The UUID of the newly created case
 * @param {string} currentUserId   - The UUID of the logged-in user (set as responsible for phase 1)
 * @returns {Promise<{error: Error|null}>}
 */
export const createCasePhasesFromTemplates = async (caseId, currentUserId) => {
  try {
    // 1. Fetch all active phase templates ordered by phase_order
    const { data: phaseTemplates, error: tplError } = await supabase
      .from('phase_templates')
      .select(`
        *,
        milestones:milestone_templates(*)
      `)
      .eq('is_active', true)
      .order('phase_order', { ascending: true });

    if (tplError) throw tplError;
    if (!phaseTemplates || phaseTemplates.length === 0) {
      console.warn('[createCasePhasesFromTemplates] No active phase templates found.');
      return { error: null };
    }

    // 2. Build phase rows
    //    Group 1 / phase_order 1  → the submission phase → in_progress, assigned to current user
    //    Everything else          → pending
    const phaseRows = phaseTemplates.map((tpl) => {
      const isSubmissionPhase = String(tpl.group ?? '') === '1' && tpl.phase_order === 1;
      return {
        case_id:             caseId,
        phase_name:          tpl.phase_name,
        phase_order:         tpl.phase_order,
        phase_template_id:   tpl.id,
        status:              isSubmissionPhase ? 'in_progress' : 'pending',
        description:         tpl.description   || null,
        group:               tpl.group         || null,
        responsible_team:    tpl.responsible_team || null,
        responsible_user_id: isSubmissionPhase ? currentUserId : null,
      };
    });

    // 3. Insert phases and get back their IDs
    const { data: insertedPhases, error: phaseInsertError } = await supabase
      .from('case_timeline_phases')
      .insert(phaseRows)
      .select('id, phase_order, group');

    if (phaseInsertError) throw phaseInsertError;

    // 4. Build a composite map:  "<group>_<phase_order>" → inserted phase id
    //
    //    BUG FIX: the old code used only phase_order as the key.
    //    Both group 1 and group 2 can share the same phase_order values
    //    (e.g. group 1 order 1 = "Soumission", group 2 order 1 = "Production").
    //    Using just phase_order meant the last write always won, attaching the
    //    submission milestones to the wrong (group-2) phase row.
    const phaseKeyToId = {};
    for (const p of insertedPhases) {
      const key = `${p.group ?? ''}_${p.phase_order}`;
      phaseKeyToId[key] = p.id;
    }

    // 5. Build milestone rows — keyed by the same composite "<group>_<phase_order>"
    const templateIds = [];
    for (const tpl of phaseTemplates) {
      for (const m of tpl.milestones || []) {
        if (m.id) templateIds.push(m.id);
      }
    }
    const optionsByTemplateId = await fetchTemplateNameOptionsByMilestoneIds(templateIds);

    const milestoneRows = [];
    for (const tpl of phaseTemplates) {
      const key     = `${tpl.group ?? ''}_${tpl.phase_order}`;
      const phaseId = phaseKeyToId[key];

      if (!phaseId) {
        console.warn(`[createCasePhasesFromTemplates] No inserted phase found for key "${key}" (template: ${tpl.phase_name})`);
        continue;
      }

      const activeMilestones = (tpl.milestones || []).filter(m => m.is_active !== false);
      for (const m of activeMilestones) {
        const templateOptions = optionsByTemplateId[m.id] || [];
        milestoneRows.push({
          phase_id:        phaseId,
          milestone_name:  resolveInitialMilestoneName(m.milestone_name, templateOptions),
          milestone_order: m.milestone_order,
          description:     m.description || null,
          is_completed:    false,
          milestone_template_id: m.id ?? null,
        });
      }
    }

    // 6. Insert milestones (only if any exist)
    if (milestoneRows.length > 0) {
      const { error: msInsertError } = await supabase
        .from('phase_milestones')
        .insert(milestoneRows);

      if (msInsertError) throw msInsertError;
    }

    return { error: null };
  } catch (err) {
    console.error('[createCasePhasesFromTemplates] Error:', err);
    return { error: err };
  }
};

/**
 * Après createCasePhasesFromTemplates : démarre v2 à la phase Conception (G1 phase 3).
 * Phases soumission + révision marquées terminées ; jalons fichiers cochés si hasFiles.
 *
 * @param {import('@supabase/supabase-js').SupabaseClient} supabaseClient
 * @param {string} caseId
 * @param {{ hasScans?: boolean, hasPhotos?: boolean, ownerUserId?: string }} options
 */
export async function bootstrapCaseAtDesignPhase(supabaseClient, caseId, options = {}) {
  const { hasScans = false, hasPhotos = false, ownerUserId = null } = options;
  const now = new Date().toISOString();

  const { data: phases, error: phasesError } = await supabaseClient
    .from('case_timeline_phases')
    .select('id, group, phase_order, milestones:phase_milestones(id, milestone_name, is_completed)')
    .eq('case_id', caseId)
    .eq('group', '1');

  if (phasesError) throw phasesError;

  const phaseByOrder = {};
  for (const p of phases || []) {
    phaseByOrder[p.phase_order] = p;
  }

  for (const order of [1, 2]) {
    const phase = phaseByOrder[order];
    if (!phase?.id) continue;
    await supabaseClient
      .from('case_timeline_phases')
      .update({
        status: 'completed',
        start_date: phase.start_date || now,
        end_date: now,
        progress_percentage: 100,
        updated_at: now,
        ...(order === 1 && ownerUserId ? { responsible_user_id: ownerUserId } : {}),
      })
      .eq('id', phase.id);
  }

  const designPhase = phaseByOrder[3];
  if (designPhase?.id) {
    await supabaseClient
      .from('case_timeline_phases')
      .update({
        status: 'in_progress',
        start_date: now,
        end_date: null,
        progress_percentage: 0,
        updated_at: now,
      })
      .eq('id', designPhase.id);
  }

  const submissionPhase = phaseByOrder[1];
  if (submissionPhase?.milestones?.length) {
    for (const m of submissionPhase.milestones) {
      let shouldComplete = false;
      if (m.milestone_name === 'Scans du patient téléchargés') shouldComplete = hasScans;
      else if (m.milestone_name === 'Photos cliniques soumises') shouldComplete = hasPhotos;
      else continue;

      await supabaseClient
        .from('phase_milestones')
        .update({
          is_completed: shouldComplete,
          completed_date: shouldComplete ? now : null,
          updated_at: now,
        })
        .eq('id', m.id);
    }
  }

  await supabaseClient
    .from('cases')
    .update({ status: 'in_progress', current_phase: 'design', updated_at: now })
    .eq('id', caseId);
}